import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from './api/client';
import { RequirePermission, Shell } from './components/Shell';
import { ToastProvider } from './components/Toast';
import { AuthProvider } from './context/AuthContext';
import { P } from './lib/permissions';
import { ContentPage } from './pages/ContentPage';
import { DashboardPage } from './pages/DashboardPage';
import { InventoryPage } from './pages/InventoryPage';
import { LoginPage } from './pages/LoginPage';
import { ManualOrderPage } from './pages/ManualOrderPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { OrdersPage, PendingFulfillmentPage } from './pages/OrdersPage';
import {
  AccountSecurityPage,
  CustomerDetailPage,
  CustomersPage,
  StaffPage,
} from './pages/PeoplePages';
import {
  BulkOperationsPage,
  LogsPage,
  SettingsPage,
  WebhooksPage,
} from './pages/PlatformPages';
import { DiscountsPage, GiftCardsPage } from './pages/PricingPages';
import { ProductEditPage } from './pages/ProductEditPage';
import { ProductsPage } from './pages/ProductsPage';
import { ReturnsPage } from './pages/ReturnsPage';
import { ShippingPage, TaxPage } from './pages/ShippingTaxPages';
import {
  BrandsPage,
  CategoriesPage,
  CollectionsPage,
  ReviewsPage,
} from './pages/TaxonomyPages';
import { AttributesPage } from './pages/AttributesPage';
import { ReportsPage } from './pages/ReportsPage';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: false,
      // 4xx hatalarinda tekrar denemek anlamsizdir.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/** Yol tanimini izinle sarmalayan kisayol. */
function guard(permissions: string[], element: React.ReactNode) {
  return <RequirePermission permissions={permissions}>{element}</RequirePermission>;
}

const router = createBrowserRouter([
  { path: '/giris', element: <LoginPage /> },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: guard([P.reports.view], <DashboardPage />) },
      { path: 'raporlar', element: guard([P.reports.view], <ReportsPage />) },

      // Satis
      { path: 'siparisler', element: guard([P.orders.view], <OrdersPage />) },
      { path: 'siparisler/yeni', element: guard([P.orders.create], <ManualOrderPage />) },
      { path: 'siparisler/:id', element: guard([P.orders.view], <OrderDetailPage />) },
      { path: 'sevkiyat', element: guard([P.orders.fulfill], <PendingFulfillmentPage />) },
      { path: 'iadeler', element: guard([P.orders.returns], <ReturnsPage />) },
      { path: 'musteriler', element: guard([P.customers.view], <CustomersPage />) },
      { path: 'musteriler/:userId', element: guard([P.customers.view], <CustomerDetailPage />) },

      // Katalog
      { path: 'urunler', element: guard([P.products.view], <ProductsPage />) },
      { path: 'urunler/:id', element: guard([P.products.view], <ProductEditPage />) },
      { path: 'kategoriler', element: guard([P.products.view], <CategoriesPage />) },
      { path: 'markalar', element: guard([P.products.view], <BrandsPage />) },
      { path: 'koleksiyonlar', element: guard([P.products.view], <CollectionsPage />) },
      { path: 'ozellikler', element: guard([P.products.view], <AttributesPage />) },
      {
        path: 'yorumlar',
        element: guard([P.products.update, P.content.manage], <ReviewsPage />),
      },
      { path: 'stok', element: guard([P.inventory.view], <InventoryPage />) },

      // Pazarlama
      { path: 'indirimler', element: guard([P.discounts.view], <DiscountsPage />) },
      { path: 'hediye-kartlari', element: guard([P.discounts.manage], <GiftCardsPage />) },
      { path: 'icerik', element: guard([P.content.view], <ContentPage />) },
      { path: 'eposta-sablonlari', element: guard([P.settings.view], <EmailTemplatesPage />) },

      // Yapilandirma
      { path: 'kargo', element: guard([P.shipping.view], <ShippingPage />) },
      { path: 'vergi', element: guard([P.settings.view], <TaxPage />) },
      { path: 'toplu-islemler', element: guard([P.products.bulk], <BulkOperationsPage />) },
      { path: 'webhooks', element: guard([P.settings.manage], <WebhooksPage />) },
      { path: 'personel', element: guard([P.security.usersManage], <StaffPage />) },
      {
        path: 'gunlukler',
        element: guard([P.security.logsView, P.security.auditView], <LogsPage />),
      },
      { path: 'ayarlar', element: guard([P.settings.view], <SettingsPage />) },
      { path: 'hesabim', element: <AccountSecurityPage /> },

      { path: '*', element: <NotFound /> },
    ],
  },
]);

function NotFound() {
  return (
    <div className="rounded-xl border border-dashed border-ink-200 bg-white px-6 py-16 text-center">
      <h1 className="text-lg font-semibold text-ink-900">Sayfa bulunamadı</h1>
      <p className="mt-1.5 text-sm text-ink-500">
        Adresi kontrol edin veya soldaki menüden devam edin.
      </p>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
